import React, { useState } from "react";
import { useQuery, useQueryClient, useQueries } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiErr, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard, Card, InfoTip, EmptyState, Disclaimer, Btn } from "@/components/common";
import { StatusChip } from "@/components/StatusChip";
import { money, taxYears, STATUS_META, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import {
  Bank, GraduationCap, ArrowsLeftRight, Warning, HandCoins, ClockCountdown, ArrowRight, Student as StudentIcon, FilePdf, CaretDown, CaretUp, ShieldCheck, CheckCircle, BellSimple, ChartLineUp, ChatCircleText, Star, ArrowsClockwise, CalendarCheck, Receipt, Info,
} from "@phosphor-icons/react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { MoneyArea, doUndoLastSettlement, DeferredRollup } from "./DashboardSettlements";
import { ContributionsCard, AdvancesRollup } from "./DashboardContributions";
import { OnboardingChecklist } from "@/components/OnboardingChecklist";
import { NewBeneficiarySetupCard } from "@/components/NewBeneficiarySetupCard";
import { DecemberDeadlineBanner, CoaSnapshotReminder } from "@/components/ComplianceNudges";
import { runProjection } from "@/lib/projectionEngine";
import { ComingUpCard, NextStepSection, useComingUp, tieUntiedNav } from "@/components/ComingUpCard";
import { withdrawalsHeadline, headlineWords, AWAITING_RATE_SENTENCE, yearLines, hasStillToCome } from "@/lib/overWithdrawnSplit";
import { DistributionSummary } from "@/components/DistributionSummary";
import { useStudentFromAddress, STUDENT_PARAM } from "@/lib/studentFromAddress";
import { cautionWords, overWords, PAID_OUTSIDE_LABEL, PAID_BY_529_LABEL, SEE_PRIVATE_SETTLEMENTS } from "@/lib/aboveAllowance";
/** Reads ?year= whenever the address names one — on arrival AND when a link
 *  on this same page names another year, as "Open 2025" in Needs your
 *  attention does. Reading it only on arrival left that button doing nothing
 *  from the Overview itself. (ZEN-782) */
export function useYearFromAddress(setYear) {
  const [searchParams, setSearchParams] = useSearchParams();
  const deepYear = searchParams.get("year");
  React.useEffect(() => {
    if (!deepYear) return;
    setYear(parseInt(deepYear));
    if (!searchParams.get(STUDENT_PARAM)) setSearchParams({}, { replace: true });
  }, [deepYear]); // eslint-disable-line react-hooks/exhaustive-deps
}

export default function Dashboard() {
  const { user, selectedStudentId } = useAuth();
  const [year, setYear] = useState(null);
  const nav = useNavigate();
  const effStudent = user.role === "student" ? user.student_id : selectedStudentId;

  // Handle deep-link: ?student=<id>&year=<yyyy>
  // The beneficiary is read by the ONE shared reader the Approvals page uses
  // too (ZEN-697), which honours only someone in this household. It also
  // clears `year` when it clears the beneficiary, so the address is rewritten
  // in one place; this effect clears `year` itself only when no beneficiary
  // is named.
  const { settling } = useStudentFromAddress({ alsoClear: ["year"] });
  useYearFromAddress(setYear);

  // default to the most recent tax year that actually has expense data
  const { data: yearProbe, isError: probeError } = useQuery({
    queryKey: ["year-probe", effStudent],
    queryFn: async () => (await api.get(`/expenses${effStudent ? `?student_id=${effStudent}` : ""}`)).data,
    retry: 1,
    // Not until a linked beneficiary is settled: the default year is chosen
    // from THIS answer, and asking about whoever was selected before the link
    // would pick their year, not the linked person's. (ZEN-697 review round 3.)
    enabled: !settling,
  });
  React.useEffect(() => {
    // `settling` too: a disabled question still hands back an answer
    // remembered for whoever was selected before the link.
    if (year != null || settling) return;
    // Resolve as soon as the probe returns OR errors — never block the dashboard on it.
    if (yearProbe !== undefined || probeError) {
      const years = (yearProbe || []).map((e) => e.eligibility_tax_year).filter(Boolean);
      setYear(years.length ? Math.max(...years) : new Date().getFullYear());
    }
  }, [yearProbe, probeError, settling]); // eslint-disable-line
  // Hard fallback: if the probe is still hanging after 6s, render with the current year
  // so the user is never stuck on a blank skeleton (inner widgets show their own Retry).
  // The six seconds start once a linked beneficiary is settled, so time spent
  // checking the link is not taken from the probe's own allowance. (ZEN-697.)
  React.useEffect(() => {
    if (year != null || settling) return;
    const t = setTimeout(() => setYear((y) => (y == null ? new Date().getFullYear() : y)), 6000);
    return () => clearTimeout(t);
  }, [year, settling]);

  if (year == null || settling) return <Skeleton />;
  return (
    <div>
      <PageHeader title="Overview" />
      {/* ═══ TAX YEAR SELECTOR ═══
          The year-end PDF sits beside the year it is for (Maryse, 26 Oct
          2026, ZEN-814). The all-children Overview has no such PDF. */}
      <div className="mb-6 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="tax-year-selector">
        <label htmlFor="dashboard-year" className="text-sm font-bold text-[#0A0A0A]">Tax year</label>
        <select id="dashboard-year" data-testid="dashboard-year" value={year} onChange={(e) => setYear(Number(e.target.value))}
          className="h-9 rounded-md border border-[#C2C9D1] bg-white px-3 text-sm font-semibold">
          {taxYears().map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        {effStudent && <YearEndPdfBtn studentId={effStudent} year={year} />}
        <span className="flex items-center gap-1 text-xs text-[#6b7280]">
          Compliance figures are calculated on a tax-year basis
          <InfoTip testid="tax-year-info" text="529 tax rules apply per calendar year. Withdrawals must match expenses in the same tax year. Switching years here updates all compliance figures on this page." />
        </span>
      </div>
      {effStudent ? <StudentDash key={effStudent} studentId={effStudent} year={year} nav={nav} /> : <PortfolioDash year={year} nav={nav} />}
    </div>
  );
}

function YearEndPdfBtn({ studentId, year }) {
  const [busy, setBusy] = useState(false);
  const dl = async (appendix) => {
    setBusy(true);
    try {
      await downloadFile("/reports/year-end", `year-end-${year}${appendix ? "-detailed" : ""}.pdf`,
        { student_id: studentId, tax_year: year, appendix });
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="year-end-pdf-btn"
          className="flex h-9 items-center gap-2 rounded-md border border-[#C2C9D1] bg-white px-3 text-sm font-semibold hover:bg-[#E9E9EB] disabled:opacity-60" disabled={busy}>
          <FilePdf size={16} weight="bold" className="text-[#F01919]" /> {busy ? "Generating…" : "Year-end PDF"} <CaretDown size={12} weight="bold" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => dl(false)} data-testid="year-end-pdf-summary">Summary one-pager</DropdownMenuItem>
        <DropdownMenuItem onClick={() => dl(true)} data-testid="year-end-pdf-detailed">Summary + expense appendix</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function FeedbackBadge({ nav }) {
  const { data } = useQuery({ queryKey: ["feedback-summary"], queryFn: async () => (await api.get("/feedback/summary")).data });
  if (!data) return null;
  return (
    <button onClick={() => nav("/feedback")} data-testid="dashboard-feedback-badge"
      className="flex h-9 items-center gap-2 rounded-md border border-[#C2C9D1] bg-white px-3 text-sm font-semibold hover:bg-[#E9E9EB]">
      <ChatCircleText size={16} weight="bold" className="text-[#034AA2]" />
      {data.avg_rating != null && <span className="flex items-center gap-0.5 text-[#F5A623]"><Star size={14} weight="fill" />{data.avg_rating}</span>}
      {data.open_count > 0
        ? <span className="rounded-full bg-[#034AA2] px-1.5 py-0.5 text-[11px] text-white">{data.open_count} open</span>
        : <span className="text-xs text-[#6b7280]">Feedback</span>}
    </button>
  );
}

function PortfolioDash({ year, nav }) {
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["dash-year", year], queryFn: async () => (await api.get(`/dashboard/tax-year/${year}`)).data });
  const { selectStudent, user: authUser } = useAuth();
  if (isLoading) return <Skeleton />;
  if (isError || !data) return <LoadError onRetry={refetch} />;
  const rows = data?.students || [];
  if (!rows.length) return <EmptyState icon={StudentIcon} title="No beneficiaries yet" subtitle="Add a beneficiary to start tracking 529 expenses." action={<Btn onClick={() => nav("/students")}>Add a beneficiary</Btn>} />;

  const agg = rows.reduce((a, r) => {
    a.eligible += r.reconciliation.total_eligible;
    a.withdrawn += r.reconciliation.total_withdrawn;
    a.wdCount += r.reconciliation.withdrawal_count || 0;
    a.out529 += r.reconciliation.total_529_outstanding;
    a.outPriv += r.reconciliation.total_private_outstanding;
    return a;
  }, { eligible: 0, withdrawn: 0, wdCount: 0, out529: 0, outPriv: 0 });

  const reconTotal = data.reconciled_distributions?.total || 0;
  const reconCount = data.reconciled_distributions?.count || 0;
  // Each row leads with what its beneficiary's 529 Withdrawals card leads
  // with; the total adds up what the rows show (ZEN-835).
  const aggHeadline = rows.reduce((a, r) => {
    if (!r.aqee) return a;
    const h = withdrawalsHeadline(r.aqee);
    a[h.kind] = (a[h.kind] || 0) + h.amount;
    return a;
  }, {});

  return (
    <div className="space-y-6">
      {authUser?.role === "owner" && <AttentionCard />}
      {authUser?.role === "owner" && <OnboardingChecklist />}
      {authUser?.role === "owner" && <NewBeneficiarySetupCard />}
      {authUser?.role === "owner" && <DecemberDeadlineBanner />}
      {authUser?.role === "owner" && <CoaSnapshotReminder />}

      {/* ═══ ZONE 1: 529 COMPLIANCE ═══ */}
      <div className="rounded-xl border border-[#034AA2]/20 bg-[#F4F7FB]/50 p-4 space-y-4" data-testid="zone-529-compliance">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-extrabold uppercase tracking-wider text-[#034AA2]">529 Compliance · Tax Records</h2>
          <div className="h-px flex-1 bg-[#034AA2]/20" />
        </div>
        <Card className="hidden overflow-x-auto p-0 sm:block">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#C2C9D1] text-left text-xs uppercase tracking-wide text-[#1f2937] [&>th]:align-top">
                <th className="px-2 py-2.5">Beneficiary</th>
                <th className="px-2 py-2.5 text-right">529-eligible expenses</th>
                <th className="px-2 py-2.5 text-right">529 Distributions</th>
                <th className="px-2 py-2.5 text-right">529 Withdrawals · {data.year}</th>
                <th className="hidden px-2 py-2.5 text-right lg:table-cell">529 Settlement Outstanding</th>
                <th className="px-2 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const rec = r.reconciliation;
                return (
                  <tr key={r.student.id} className="border-t border-[#C2C9D1] hover:bg-[#E9E9EB]/60" data-testid={`portfolio-row-${r.student.id}`}>
                    <td className="px-2 py-2.5 font-semibold">
                      {r.student.full_name}
                      {r.student.archived && <span className="ml-1.5 rounded bg-[#E9E9EB] px-1.5 py-0.5 text-[10px] font-medium text-[#6b7280]" data-testid={`archived-badge-${r.student.id}`}>Archived</span>}
                    </td>
                    <td className="px-2 py-2.5 text-right tnum">{money(rec.total_eligible, "USD")}</td>
                    <td className="px-2 py-2.5 text-right tnum">{money(rec.total_withdrawn, "USD")}</td>
                    <td className="px-2 py-2.5 text-right"><WithdrawalsHeadlineCell aqee={r.aqee} year={data.year} testid={`portfolio-withdrawals-${r.student.id}`} /></td>
                    <td className="hidden px-2 py-2.5 text-right tnum lg:table-cell">
                      <span className={rec.total_529_outstanding > 0.005 ? "text-[#D97706]" : "text-[#1f2937]"}>{money(rec.total_529_outstanding, "USD")}</span>
                      <AboveAllowanceShort status={rec.rent_and_food_above} year={data.year} testid={`portfolio-rb-above-${r.student.id}`} />
                    </td>
                    <td className="px-2 py-2.5 text-right">
                      <button onClick={() => { selectStudent(r.student.id); }} className="inline-flex items-center gap-1 text-sm font-semibold text-[#034AA2] hover:underline" data-testid={`view-student-${r.student.id}`}>
                        View <ArrowRight size={14} weight="bold" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-[#034AA2]/30 bg-[#F4F7FB]" data-testid="portfolio-total-row">
                <td className="px-2 py-2.5 font-bold text-[#034AA2]">All beneficiaries</td>
                <td className="px-2 py-2.5 text-right tnum font-bold">{money(agg.eligible, "USD")}</td>
                <td className="px-2 py-2.5 text-right tnum font-bold">
                  <div>{money(agg.withdrawn, "USD")}</div>
                  <div className="mt-0.5 flex items-center justify-end gap-1 text-[11px] font-normal text-[#D97706]">
                    {money(reconTotal, "USD")} · {reconCount} of {agg.wdCount} reconciled
                    <InfoTip testid="recon-info-tip" size={11} text="Reconciled means a 529 distribution's date received is recorded and it's matched to your eligible expenses. Your plan issues the 1099-Q in January of the following year — when it arrives, these are the figures you'll check it against." />
                  </div>
                </td>
                <td className="px-2 py-2.5 text-right"><WithdrawalsHeadlineTotal agg={aggHeadline} year={data.year} testid="portfolio-withdrawals-total" /></td>
                <td className="hidden px-2 py-2.5 text-right tnum font-bold lg:table-cell">
                  <span className={agg.out529 > 0.005 ? "text-[#D97706]" : ""}>{money(agg.out529, "USD")}</span>
                </td>
                <td className="px-2 py-2.5"></td>
              </tr>
            </tfoot>
          </table>
        </Card>
        {/* Mobile cards for 529 table */}
        <div className="space-y-3 sm:hidden">
          {rows.map((r) => {
            const rec = r.reconciliation;
            return (
              <Card key={r.student.id} className="p-4" data-testid={`portfolio-row-${r.student.id}`}>
                <div className="flex items-center justify-between">
                  <div className="font-semibold">
                    {r.student.full_name}
                    {r.student.archived && <span className="ml-1.5 rounded bg-[#E9E9EB] px-1.5 py-0.5 text-[10px] font-medium text-[#6b7280]" data-testid={`archived-badge-${r.student.id}`}>Archived</span>}
                  </div>
                  <button onClick={() => { selectStudent(r.student.id); }} className="inline-flex items-center gap-1 text-sm font-semibold text-[#034AA2] hover:underline" data-testid={`view-student-${r.student.id}`}>
                    View <ArrowRight size={14} weight="bold" />
                  </button>
                </div>
                <div className="mt-2 space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-[#6b7280]">529-eligible expenses</span><span className="tnum font-semibold">{money(rec.total_eligible, "USD")}</span></div>
                  <div className="flex justify-between"><span className="text-[#6b7280]">529 Distributions</span><span className="tnum font-semibold">{money(rec.total_withdrawn, "USD")}</span></div>
                  <div className="flex justify-between gap-3"><span className="shrink-0 whitespace-nowrap text-[#6b7280]">529 Withdrawals · {data.year}</span><WithdrawalsHeadlineCell aqee={r.aqee} year={data.year} testid={`portfolio-withdrawals-mobile-${r.student.id}`} /></div>
                  <div className="flex justify-between"><span className="text-[#6b7280]">Settlement Outstanding</span><span className={`tnum font-semibold ${rec.total_529_outstanding > 0.005 ? "text-[#D97706]" : ""}`}>{money(rec.total_529_outstanding, "USD")}</span></div>
                  <AboveAllowanceShort status={rec.rent_and_food_above} year={data.year} testid={`portfolio-rb-above-mobile-${r.student.id}`} />
                </div>
              </Card>
            );
          })}
          <Card className="border-t-2 border-[#034AA2]/30 bg-[#F4F7FB] p-4" data-testid="portfolio-total-mobile">
            <div className="font-bold text-[#034AA2]">All beneficiaries</div>
            <div className="mt-2 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-[#6b7280]">529-eligible expenses</span><span className="tnum font-bold">{money(agg.eligible, "USD")}</span></div>
              <div className="flex justify-between">
                <span className="text-[#6b7280]">529 Distributions</span>
                <div className="text-right">
                  <span className="tnum font-bold">{money(agg.withdrawn, "USD")}</span>
                  <div className="mt-0.5 flex items-center justify-end gap-1 text-[11px] text-[#D97706]">
                    {money(reconTotal, "USD")} · {reconCount} of {agg.wdCount} reconciled
                    <InfoTip testid="recon-info-tip-mobile" size={11} text="Reconciled means a 529 distribution's date received is recorded and it's matched to your eligible expenses. Your plan issues the 1099-Q in January of the following year — when it arrives, these are the figures you'll check it against." />
                  </div>
                </div>
              </div>
              <div className="flex justify-between gap-3"><span className="shrink-0 whitespace-nowrap text-[#6b7280]">529 Withdrawals · {data.year}</span><WithdrawalsHeadlineTotal agg={aggHeadline} year={data.year} testid="portfolio-withdrawals-total-mobile" /></div>
              <div className="flex justify-between"><span className="text-[#6b7280]">Settlement Outstanding</span><span className={`tnum font-bold ${agg.out529 > 0.005 ? "text-[#D97706]" : ""}`}>{money(agg.out529, "USD")}</span></div>
            </div>
          </Card>
        </div>
        <ProjectionsRollupCard students={rows.map((r) => r.student)} nav={nav} />
      </div>

      {/* ═══ ZONE 2: FAMILY MONEY TOOLS ═══ */}
      <FamilyMoneyZone rows={rows} agg={agg} />

      <Disclaimer text={data?.disclaimer} />
    </div>
  );
}

// One beneficiary's 529 Withdrawals headline, as its card shows it (ZEN-835).
const HEADLINE_COLOUR = { beyond: "text-[#F01919]", ahead: "text-[#B45309]", awaiting: "text-[#B45309]", safe: "text-[#077A39]" };
function WithdrawalsHeadlineCell({ aqee, year, testid }) {
  if (!aqee) return <span className="tnum text-[#6b7280]" data-testid={testid}>—</span>;
  const h = withdrawalsHeadline(aqee);
  const colour = h.kind === "safe" && !(h.amount > 0.005) ? "text-[#1f2937]" : HEADLINE_COLOUR[h.kind];
  return (
    <span className={`inline-block text-right ${colour}`} data-testid={testid} data-kind={h.kind}>
      <span className="tnum font-semibold">{money(h.amount, "USD")}</span>
      <span className="block text-[11px] font-normal leading-snug sm:whitespace-nowrap">{headlineWords(h.kind, year)}</span>
    </span>
  );
}

function WithdrawalsHeadlineTotal({ agg, year, testid }) {
  const kinds = ["beyond", "ahead", "awaiting", "safe"].filter((k) => (agg[k] || 0) > 0.005);
  if (!kinds.length) return <span className="tnum font-bold" data-testid={testid}>{money(0, "USD")}</span>;
  return (
    <span className="inline-block text-right" data-testid={testid}>
      {kinds.map((k) => (
        <span key={k} className={`block [&+&]:mt-1 ${HEADLINE_COLOUR[k]}`} data-testid={`${testid}-${k}`}>
          <span className="tnum font-bold">{money(agg[k], "USD")}</span>{" "}
          <span className="block text-[11px] font-normal leading-snug sm:whitespace-nowrap">{headlineWords(k, year)}</span>
        </span>
      ))}
    </span>
  );
}

// THE FAMILY MONEY TOOLS BAND, one shell for both Overviews: the same title,
// badge, sentence and Show / Hide, starting closed (Maryse, 26 and 28 Oct
// 2026, ZEN-814 point 2 and Q6). `actions` sit beside Show / Hide.
export function FamilyMoneyBand({ actions, children }) {
  const [open, setOpen] = useState(false);
  const Chevron = open ? CaretUp : CaretDown;
  return (
    <div className="rounded-xl border border-[#C2C9D1] bg-white" data-testid="zone-family-money">
      {/* Collapsed header — always visible */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-3">
        <h2 className="text-sm font-extrabold uppercase tracking-wider text-[#6b7280]">Family Money Tools</h2>
        <span className="rounded-full border border-[#C2C9D1] bg-[#F7F8FA] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#6b7280]" data-testid="family-money-pill">Optional &middot; Not tax records</span>
        <div className="h-px flex-1 bg-[#C2C9D1]" />
        {actions}
        <button onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1 shrink-0 rounded-md px-2.5 py-1.5 text-xs font-bold text-[#6b7280] hover:bg-[#E9E9EB] transition-colors"
          data-testid="family-money-toggle">
          {open ? "Hide" : "Show"} <Chevron size={14} weight="bold" />
        </button>
      </div>
      <div className="border-t border-[#C2C9D1] px-4 py-2">
        <p className="text-xs text-[#6b7280]" data-testid="family-money-note">
          Track costs a 529 can&apos;t cover &mdash; transportation, student health insurance, and personal costs beyond what a 529 covers for housing and food. Never touches your 529 balance or tax reports.
        </p>
      </div>
      {open && <div className="border-t border-[#C2C9D1] p-4 space-y-4" data-testid="family-money-body">{children}</div>}
    </div>
  );
}

function FamilyMoneyZone({ rows, agg }) {
  const { selectStudent } = useAuth();
  return (
    <FamilyMoneyBand>
        <>
          <Card className="hidden overflow-x-auto p-0 sm:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#E9E9EB] text-left text-xs uppercase tracking-wide text-[#6b7280] [&>th]:align-top">
                  <th className="px-2 py-2.5">Beneficiary</th>
                  <th className="px-2 py-2.5 text-right">Private Settlement Outstanding</th>
                  <th className="px-2 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const rec = r.reconciliation;
                  return (
                    <tr key={r.student.id} className="border-t border-[#E9E9EB] hover:bg-[#E9E9EB]/40" data-testid={`private-row-${r.student.id}`}>
                      <td className="px-2 py-2.5 font-semibold">
                        {r.student.full_name}
                        {r.student.archived && <span className="ml-1.5 rounded bg-[#E9E9EB] px-1.5 py-0.5 text-[10px] font-medium text-[#6b7280]" data-testid={`archived-badge-private-${r.student.id}`}>Archived</span>}
                      </td>
                      <td className="px-2 py-2.5 text-right tnum">
                        <span className={rec.total_private_outstanding > 0.005 ? "text-[#D97706]" : "text-[#1f2937]"}>{money(rec.total_private_outstanding, "USD")}</span>
                      </td>
                      <td className="px-2 py-2.5 text-right">
                        <button onClick={() => { selectStudent(r.student.id); }} className="inline-flex items-center gap-1 text-sm font-semibold text-[#034AA2] hover:underline" data-testid={`view-private-${r.student.id}`}>
                          View <ArrowRight size={14} weight="bold" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-[#6b7280]/30 bg-[#F7F8FA]" data-testid="private-total-row">
                  <td className="px-2 py-2.5 font-bold text-[#6b7280]">All beneficiaries</td>
                  <td className="px-2 py-2.5 text-right tnum font-bold">
                    <span className={agg.outPriv > 0.005 ? "text-[#D97706]" : ""}>{money(agg.outPriv, "USD")}</span>
                  </td>
                  <td className="px-2 py-2.5"></td>
                </tr>
              </tfoot>
            </table>
          </Card>
          {/* Mobile cards for private table */}
          <div className="space-y-3 sm:hidden">
            {rows.map((r) => {
              const rec = r.reconciliation;
              return (
                <Card key={r.student.id} className="p-4" data-testid={`private-row-${r.student.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-semibold">
                      {r.student.full_name}
                      {r.student.archived && <span className="ml-1.5 rounded bg-[#E9E9EB] px-1.5 py-0.5 text-[10px] font-medium text-[#6b7280]" data-testid={`archived-badge-private-${r.student.id}`}>Archived</span>}
                    </div>
                    <button onClick={() => { selectStudent(r.student.id); }} className="inline-flex items-center gap-1 text-sm font-semibold text-[#034AA2] hover:underline" data-testid={`view-private-${r.student.id}`}>
                      View <ArrowRight size={14} weight="bold" />
                    </button>
                  </div>
                  <div className="mt-2 text-sm">
                    <div className="flex justify-between"><span className="text-[#6b7280]">Private Settlement Outstanding</span><span className={`tnum font-semibold ${rec.total_private_outstanding > 0.005 ? "text-[#D97706]" : ""}`}>{money(rec.total_private_outstanding, "USD")}</span></div>
                  </div>
                </Card>
              );
            })}
            <Card className="border-t-2 border-[#6b7280]/30 bg-[#F7F8FA] p-4" data-testid="private-total-mobile">
              <div className="font-bold text-[#6b7280]">All beneficiaries</div>
              <div className="mt-2 text-sm">
                <div className="flex justify-between"><span className="text-[#6b7280]">Private Settlement Outstanding</span><span className={`tnum font-bold ${agg.outPriv > 0.005 ? "text-[#D97706]" : ""}`}>{money(agg.outPriv, "USD")}</span></div>
              </div>
            </Card>
          </div>
          <DeferredRollup items={rows.flatMap((r) => (r.reconciliation.area_private || []).filter((c) => c.net > 0.005).map((c) => ({ ...c, student: r.student.full_name, student_id: r.student.id })))} />
        </>
    </FamilyMoneyBand>
  );
}

function StudentDash({ studentId, year, nav }) {
  const qc = useQueryClient();
  const { user: authUser } = useAuth();
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["dash-student", studentId, year], queryFn: async () => (await api.get(`/dashboard/student/${studentId}?tax_year=${year}`)).data });
  const { data: allStudents = [] } = useQuery({ queryKey: ["students"], queryFn: async () => (await api.get("/students")).data });
  const beneficiaryName = allStudents.find((s) => s.id === studentId)?.full_name || allStudents.find((s) => s.id === studentId)?.name || "";
  // The name the page calls the child by: the nickname, else the first name —
  // the same rule the server uses for the payer rows (_reconcile nick).
  const thisChild = allStudents.find((s) => s.id === studentId);
  const childName = thisChild?.nickname || (thisChild?.full_name || "").split(" ")[0] || "";  if (isLoading) return <Skeleton />;
  if (isError || !data) return <LoadError onRetry={refetch} />;
  const rec = data.reconciliation;
  // Card visibility follows ACTIVE plans only: a beneficiary whose every plan
  // is archived must not see a live balance card asking for a checkpoint on
  // accounts that are deliberately closed.
  const activePlanRows = (data.balances || []).filter((b) => !b.account?.archived);
  const hasBalances = activePlanRows.length > 0;
  const showWithdrawalsCard = !!data.aqee && (authUser?.role === "owner" || (authUser?.role === "student" && hasBalances));
  // Every plan counts, not just the first (ZEN-69). A beneficiary with two 529
  // accounts used to see only balances[0] presented as THE balance — the second
  // plan silently missing from a money figure. Sum the anchored plans; carry
  // how many have no balance entered so the card can say the total is partial
  // rather than passing a subtotal off as whole (same honesty rule as
  // projections' balance_complete).
  // Archived plans are closed or rolled over — summing a rollover SOURCE next
  // to its destination counts the same dollars twice. They stay visible in
  // Accounts; they just don't participate in the live balance figure.
  const planBalances = activePlanRows.map((b) => b.balance).filter(Boolean);
  const anchoredPlans = planBalances.filter((b) => b.anchored);
  const unanchoredPlanCount = planBalances.length - anchoredPlans.length;
  const bal = anchoredPlans.length > 0 ? {
    anchored: true,
    estimated: anchoredPlans.reduce((s, b) => s + (b.estimated || 0), 0),
    // "As of" the OLDEST anchor among the plans: the summed figure is only as
    // fresh as its stalest component, and overstating freshness on a money
    // number is the exact sin the old base_date bump committed.
    latest_date: anchoredPlans.map((b) => b.latest_date).filter(Boolean).sort()[0] || null,
  } : null;
  const balSub = !bal
    ? "Add a balance checkpoint"
    : unanchoredPlanCount > 0
      ? `${unanchoredPlanCount} of ${planBalances.length} plans missing a balance`
      : (bal.latest_date ? `As of ${fmtDate(bal.latest_date)}` : "Add a balance checkpoint");
  return (
    <div className="space-y-6">
      {/* ═══ NEEDS ATTENTION CARD — owner only, above compliance ═══ */}
      {authUser?.role === "owner" && <AttentionCard studentId={studentId} />}

      {/* ═══ 529 COMPLIANCE ZONE ═══ */}
      <div className="space-y-6" data-testid="zone-529-compliance">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-extrabold uppercase tracking-wider text-[#034AA2]" data-testid="zone-529-heading">
            {["529 Compliance", childName, year].filter(Boolean).join(" · ")}
          </h2>
          <div className="h-px flex-1 bg-[#034AA2]/20" />
        </div>

        <div className={`grid grid-cols-1 gap-4 @sm:grid-cols-2 ${hasBalances ? "@3xl:grid-cols-4" : "@3xl:grid-cols-3"}`}>
          {/* Without an anchor there is no balance to estimate — show "—" and ask
              for one, rather than a confident-looking $0.00. */}
          {hasBalances && (
            <StatCard label="529 estimated balance" value={bal ? money(bal.estimated, "USD") : "—"}
              sub={balSub} icon={Bank} testid="stat-balance" />
          )}
          <WithdrawalTile year={year} withdrawn={rec.total_withdrawn} withdrawalCount={rec.withdrawal_count} reconciledTotal={data.reconciled_distributions?.total || 0} reconciledCount={data.reconciled_distributions?.count || 0} testid="stat-withdrawals" />
          <StatCard label="529-eligible expenses" value={money(rec.total_eligible, "USD")} sub={`${rec.expense_count} expenses${rec.estimated_eligible > 0.005 ? ` · ≈${money(rec.estimated_eligible, "USD")} estimated` : ""}`} icon={GraduationCap} testid="stat-eligible" />
          <StatCard label="Pending reimbursement" value={money(data.pending_reimbursements.total, "USD")} sub={`${data.pending_reimbursements.count} expenses awaiting`} icon={ClockCountdown} testid="stat-pending" amber />
        </div>

        {/* A TOTAL THAT IS SHORT HAS TO SAY SO. (ZEN-632)

            An expense the application could not convert into US dollars is
            stored with no dollar figure on purpose, and every figure in the
            cards above deliberately leaves it out — adding it in as zero would
            be a wrong number stated confidently on a tax figure, which is the
            one thing a family cannot spot for themselves.

            So the count comes out here, in the same words and the same amber
            the Gifts and Contributions screens already use for the identical
            situation ("2 gifts need a manual rate — edit to fix"). Excluded and
            said out loud; never silently zero. */}
        {rec.unpriced_expense_count > 0 && (
          <div className="flex items-start gap-2 rounded-lg border border-[#D97706]/30 bg-[#FEF3C7] px-4 py-3" data-testid="unpriced-expenses-note">
            <Warning size={18} weight="bold" className="mt-0.5 shrink-0 text-[#D97706]" />
            <div className="text-xs text-[#92400E]">
              <span className="font-bold">
                {rec.unpriced_expense_count} expense{rec.unpriced_expense_count !== 1 ? "s" : ""} could not be converted into US dollars
              </span>{" "}
              — no exchange rate was available, so {rec.unpriced_expense_count !== 1 ? "they are" : "it is"} not counted in any total above or in your tax documents. <a href={unpricedExpensesLink(studentId, year)} className="font-semibold text-[#034AA2] hover:underline">Show me which {rec.unpriced_expense_count !== 1 ? "ones" : "one"} →</a>
            </div>
          </div>
        )}

        {/* ZEN-818: right under the Pending reimbursement card it explains,
            and the first mention of COA on this page, so it is spelled out. */}
        <AboveAllowanceNotice status={rec.rent_and_food_above} year={year} />

        {authUser?.role === "owner" && <DecemberDeadlineBanner studentId={studentId} />}
        {authUser?.role === "owner" && <CoaSnapshotReminder studentId={studentId} />}

        {authUser?.role === "owner" && <YearEndReadinessCard studentId={studentId} year={year} />}

        {showWithdrawalsCard && <SafeToWithdrawCard aqee={data.aqee} rec={data.reconciliation} canEditSchools={authUser?.role === "owner"} year={year} nav={nav} studentId={studentId} />}

        <div className="grid grid-cols-1 gap-4 @3xl:grid-cols-2">
          <DistributionSummary statutoryCaps={data.statutory_caps} groupCaps={data.group_caps} aqee={data.aqee} beneficiaryName={beneficiaryName} taxYear={year} compact />
          {rec.paid_by && <PaidByCard paidBy={rec.paid_by} />}
        </div>

        {data.aqee && data.aqee.aotc_llc_reserved > 0 && (
          <div className="flex items-center gap-2 rounded-lg border border-[#0369A1]/20 bg-[#0369A1]/5 px-4 py-2.5" data-testid="aotc-reserved-callout">
            <span className="shrink-0 rounded-full bg-[#0369A1] px-2 py-0.5 text-[10px] font-bold text-white">AOTC</span>
            <span className="text-xs font-semibold text-[#374151]">Reserved for AOTC (paid privately): <span className="tnum">{money(data.aqee.aotc_llc_reserved, "USD")}</span></span>
            <InfoTip testid="aotc-reserved-info" width="w-80">
              This is 529-eligible spending you&apos;re paying without 529 funds, so it can count toward the American Opportunity Tax Credit (AOTC) — up to $4,000 of qualified costs — instead of the 529. Keep your receipts. Guidance only, not tax advice — rules change; confirm with your tax advisor.
            </InfoTip>
          </div>
        )}

        {/* AOTC reconciliation alerts.
            aotc_marked != null on BOTH: the server returns null when the year's
            reservations cannot be totalled (aotc_marked_unreadable), and null
            compares false-then-true in JavaScript — so without this guard an
            unreadable year fires "reserve has room" below and tells the owner to
            set MORE aside on the strength of a number the server just said it
            could not read. The banner that SHOULD show in that case, and this
            whole rule moving into lib/aotcBannerState with its own tests, are
            ZEN-222.

            NO aotc_llc_reserved > 0 ON THE FIRST ONE, and that omission is the
            point. The server stopped skipping the figure at a zero target
            (routes/scholarships.py) precisely so this case could be seen — an
            owner who drops an eligible beneficiary's target to $0 while
            reservations are still outstanding is the person most in need of
            telling. Leaving the condition here would have made the server-side
            fix invisible, which is a fix that is not a fix. `marked > target`
            covers it on its own: at a target of $0, anything reserved is over.
            (Caught by the pre-merge diff review — the claim had been made in a
            commit message before it was true on screen.) */}
        {data.aqee && data.aqee.aotc_eligibility === "yes" && data.aqee.aotc_marked != null && data.aqee.aotc_marked > data.aqee.aotc_llc_reserved && (
          <div className="flex items-start gap-2 rounded-lg border border-[#D97706]/30 bg-[#FEF3C7] px-4 py-3" data-testid="aotc-over-committed">
            <Warning size={18} weight="bold" className="mt-0.5 shrink-0 text-[#D97706]" />
            <div className="text-xs text-[#92400E]">
              {/* States the fact, not a cause. The previous wording opened "you
                  lowered ... 's target", which is one explanation among several
                  — a family can now meet this banner having lowered nothing,
                  because the total itself started counting expenses recorded but
                  not yet paid. Telling an owner they did something they did not
                  do, about a tax figure, is worse than saying less. */}
              <span className="font-bold">AOTC reserve needs review</span> — {beneficiaryName || "this beneficiary"}&apos;s {year} target is {money(data.aqee.aotc_llc_reserved, "USD")}, but {money(data.aqee.aotc_marked, "USD")} of expenses are set aside for the credit. Reservations on expenses you have recorded but not yet paid count too. <a href={`/expenses?student_id=${studentId}&tab=529&tax_year=${year}`} className="font-semibold text-[#034AA2] hover:underline">Review which to keep reserved →</a>
            </div>
          </div>
        )}
        {data.aqee && data.aqee.aotc_eligibility === "yes" && data.aqee.aotc_marked != null && data.aqee.aotc_llc_reserved > 0 && data.aqee.aotc_marked < data.aqee.aotc_llc_reserved && (
          <div className="flex items-start gap-2 rounded-lg border border-[#D97706]/30 bg-[#FEF3C7] px-4 py-3" data-testid="aotc-under-filled">
            <Warning size={18} weight="bold" className="mt-0.5 shrink-0 text-[#D97706]" />
            <div className="text-xs text-[#92400E]">
              <span className="font-bold">AOTC reserve has room</span> — {beneficiaryName || "this beneficiary"}&apos;s {money(data.aqee.aotc_llc_reserved, "USD")} target for {year} is only {money(data.aqee.aotc_marked, "USD")} filled. <a href={`/expenses?student_id=${studentId}&tab=529&tax_year=${year}`} className="font-semibold text-[#034AA2] hover:underline">Mark more out-of-pocket expenses to claim the full credit →</a>
            </div>
          </div>
        )}

        {/* The combined 529 Withdrawals card carries the next step; a page
            that does not show that card keeps the Coming up box. */}
        {!showWithdrawalsCard && <ComingUpCard studentId={studentId} onWithdraw={() => nav("/withdrawals")} nav={nav} />}

        <CoaCaps caps={data.coa_caps} year={year} tuitionClaimed={data.tuition_claimed} payers={rec.rent_and_food_above?.schools} />

        <div className="grid grid-cols-1 gap-4 @xl:grid-cols-3">
          <div className="space-y-4 @xl:col-span-2">
            <MoneyArea title="529 Settlements"
              cards={rec.area_529} testid="area-529" studentId={studentId} area="529" taxYear={year}
              onUndo={(c) => doUndoLastSettlement(qc, studentId, c, "529")} />
          </div>
          <StatusCard data={data} kind="529" isOwner={authUser?.role === "owner"} nav={nav} />
        </div>

        {data.lump_sum_warnings?.length > 0 && (
          <Card data-testid="lump-warnings">
            <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-[#6b7280]">Lump-sum justification</h3>
            {data.lump_sum_warnings.map((w) => (
              <div key={w.withdrawal_id} className="mb-2 rounded-md border border-[#F68F51] bg-[#F68F51]/10 px-3 py-2 text-sm">
                Lump sum {money(w.amount, "USD")} ({w.tax_year}) — justified {money(w.justified, "USD")}, <b className="text-[#7D3306]">remaining {money(w.remaining, "USD")}</b> to cover with eligible expenses.
              </div>
            ))}
          </Card>
        )}

      </div>

      {/* ═══ FAMILY MONEY TOOLS BAND — the same band as the all-children
          Overview, starting closed (ZEN-814 point 2, Q6) ═══ */}
      <FamilyMoneyBand actions={<FamilyMoneyPdfBtn studentId={studentId} year={year} />}>
        <div className="grid grid-cols-1 gap-4 @xl:grid-cols-3">
          <div className="space-y-4 @xl:col-span-2">
            <MoneyArea title="Private Settlements"
              cards={rec.area_private} testid="area-private" onUndo={(c) => doUndoLastSettlement(qc, studentId, c)} studentId={studentId} taxYear={year} />
          </div>
          <StatusCard data={data} kind="non529" isOwner={authUser?.role === "owner"} nav={nav} />
        </div>
        <OwnMoneyCard name={childName} year={year} own={rec.student_paid_not_reimbursed} others={rec.covered_not_reimbursed || []} />
        <ContributionsCard gifts={rec.gifts} studentId={studentId} />
        <AdvancesRollup studentId={studentId} />
      </FamilyMoneyBand>

      <Disclaimer text={data.disclaimer} />
    </div>
  );
}


function YearEndReadinessCard({ studentId, year }) {
  const { data, isLoading } = useQuery({
    queryKey: ["year-end-readiness", studentId, year],
    queryFn: async () => (await api.get(`/dashboard/year-end-readiness/${studentId}?tax_year=${year}`)).data,
  });
  const nav = useNavigate();

  if (isLoading || !data) return null;

  const { is_past, days_left, missing_receipt_count, unreconciled_gap, all_set } = data;

  return (
    <Card className="overflow-hidden" data-testid="year-end-readiness-card">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <CalendarCheck size={20} weight="bold" className="shrink-0 text-[#034AA2]" />
        <h3 className="text-sm font-bold uppercase tracking-wide text-[#034AA2]">Year-End Readiness · {year}</h3>
      </div>

      {/* Countdown or closed state */}
      {is_past ? (
        <div className="mb-3 rounded-lg bg-[#E9E9EB] px-4 py-2.5" data-testid="year-end-closed">
          <span className="text-sm font-semibold text-[#6b7280]">Tax year {year} is closed.</span>
        </div>
      ) : (
        <div className="mb-3 rounded-lg bg-[#F4F7FB] px-4 py-2.5" data-testid="year-end-countdown">
          <span className="text-lg font-extrabold tnum text-[#034AA2]">{days_left}</span>
          <span className="ml-1.5 text-sm font-semibold text-[#374151]">day{days_left !== 1 ? "s" : ""} left in {year}</span>
        </div>
      )}

      {/* Deadline note */}
      <p className="mb-4 text-xs text-[#374151]" data-testid="year-end-deadline-note">
        {/* Approved 26 Oct 2026 (ZEN-814): no numbers side by side; the card's
            heading carries the year. */}
        Every 529 withdrawal made this year must match this year&apos;s expenses by December 31.
      </p>

      {/* Proactive checks */}
      {all_set ? (
        <div className="flex items-center gap-2 rounded-lg border border-[#077A39]/20 bg-[#077A39]/5 px-4 py-3" data-testid="year-end-all-set">
          <CheckCircle size={20} weight="fill" className="shrink-0 text-[#077A39]" />
          <span className="text-sm font-semibold text-[#077A39]">You&apos;re all set for {year}</span>
        </div>
      ) : (
        <div className="space-y-2.5" data-testid="year-end-action-items">
          {/* Missing receipts */}
          {missing_receipt_count > 0 && (
            <div className="flex flex-wrap items-start justify-between gap-2 rounded-lg border border-[#D97706]/20 bg-[#D97706]/5 px-4 py-2.5" data-testid="year-end-missing-receipts">
              <div className="flex min-w-0 items-center gap-2">
                <Receipt size={18} weight="bold" className="shrink-0 text-[#D97706]" />
                <span className="text-sm text-[#92400E]">
                  <span className="font-bold tnum">{missing_receipt_count}</span> {missing_receipt_count !== 1 ? "expenses eligible for the 529 are" : "expense eligible for the 529 is"} missing a receipt
                </span>
              </div>
              <button
                onClick={() => nav(`/expenses?student_id=${studentId}&tax_year=${year}&tab=529&missing_receipt=1`)}
                className="shrink-0 text-sm font-semibold text-[#034AA2] hover:underline"
                data-testid="year-end-missing-receipts-link"
              >
                View →
              </button>
            </div>
          )}

          {/* Unreconciled withdrawals gap */}
          {unreconciled_gap > 0.005 && (
            <div className="flex flex-wrap items-start justify-between gap-2 rounded-lg border border-[#D97706]/20 bg-[#D97706]/5 px-4 py-2.5" data-testid="year-end-unreconciled">
              <div className="flex min-w-0 items-center gap-2">
                <Bank size={18} weight="bold" className="shrink-0 text-[#D97706]" />
                <span className="text-sm text-[#92400E]">
                  <span className="font-bold tnum">{money(unreconciled_gap, "USD")}</span> in 529-eligible expenses not yet matched by withdrawals
                </span>
              </div>
              <button
                onClick={() => nav("/withdrawals")}
                className="shrink-0 text-sm font-semibold text-[#034AA2] hover:underline"
                data-testid="year-end-unreconciled-link"
              >
                Record withdrawal →
              </button>
            </div>
          )}
        </div>
      )}

      {/* Disclaimer */}
      <div className="mt-4 flex items-start gap-2 rounded-lg border border-[#0369A1]/15 bg-[#0369A1]/5 px-3 py-2.5" data-testid="year-end-disclaimer">
        <Info size={16} weight="fill" className="mt-0.5 shrink-0 text-[#0369A1]" />
        <p className="text-[11px] leading-relaxed text-[#374151]">
          This is a reminder only. Confirm 529 withdrawal decisions with your tax advisor. Expenses should be paid in the same calendar year as the withdrawal.
        </p>
      </div>
    </Card>
  );
}


// ZEN-818: the yellow (not taken out yet) and red (already beyond the year's
// qualified costs) boxes. The figures and the test are the server's; the words
// are lib/aboveAllowance's.
function AboveAllowanceNotice({ status, year }) {
  if (!status?.caution && !status?.over) return null;
  const c = status.caution && cautionWords(status.caution, { firstMention: true });
  const o = status.over && overWords(status.over, year, { firstMention: !c });
  return (
    <div className="space-y-2" data-testid="rb-above-notice">
      {c && (
        <div className="flex items-start gap-2 rounded-lg border border-[#D97706]/30 bg-[#FEF3C7] px-4 py-3" data-testid="rb-above-caution">
          <Warning size={18} weight="bold" className="mt-0.5 shrink-0 text-[#D97706]" />
          <div className="text-xs text-[#92400E]"><span className="font-bold">{c.title}</span> {c.body}</div>
        </div>
      )}
      {o && (
        <div className="flex items-start gap-2 rounded-lg border border-[#F01919]/30 bg-[#FFF5F5] px-4 py-3" data-testid="rb-above-over">
          <Warning size={18} weight="bold" className="mt-0.5 shrink-0 text-[#F01919]" />
          <div className="text-xs text-[#991B1B]"><span className="font-bold">{o.title}</span> {o.body}</div>
        </div>
      )}
    </div>
  );
}

// ZEN-818: the same two boxes as a short line under the All beneficiaries
// page's 529 Settlement Outstanding figure (situations A and D only).
function AboveAllowanceShort({ status, year, testid }) {
  if (!status?.caution && !status?.over) return null;
  return (
    <div className="mt-0.5 space-y-0.5 text-left text-[11px] font-normal leading-snug" data-testid={testid}>
      {status.caution && <div className="text-[#92400E]">{cautionWords(status.caution).short}</div>}
      {status.over && <div className="text-[#991B1B]">{overWords(status.over, year).short}</div>}
    </div>
  );
}

function CoaCaps({ caps, year, tuitionClaimed, payers }) {
  if (!caps?.length && !tuitionClaimed) return null;
  const RB_OVER_MSG = `Room & board recorded for tax year ${year} exceeds the combined COA allowance for all months enrolled at least half-time (accounting for the IRS \u201cgreater of\u201d rule for school-owned housing). Amounts above the cap are not qualified 529 room & board expenses. Review this beneficiary\u2019s school periods and COA figures for possible entry errors; if the COA is genuinely exceeded, avoid further 529 withdrawals for room & board this year.`;
  return (
    <Card data-testid="coa-caps-card">
      <h3 className="mb-1 text-sm font-bold uppercase tracking-wide text-[#6b7280]">529 cost-of-attendance · {year}</h3>
      <p className="mb-3 text-xs text-[#6b7280]">Room &amp; board qualifies up to the <b>greater of</b> the school&apos;s COA allowance or actual school-housing charges (IRS Pub 970). Off-campus beneficiaries are capped at the COA allowance. Tuition, fees, books, and supplies are uncapped.</p>

      {tuitionClaimed > 0 && (
        <div className="mb-3 rounded-lg border border-[#C2C9D1] p-3" data-testid="tuition-tracked">
          <div className="text-sm font-bold">Tuition, fees, books &amp; supplies</div>
          <div className="mt-1 text-xs text-[#6b7280]">Tracked total — no COA cap applies</div>
          <div className="mt-1.5 text-lg font-bold tnum text-[#1f2937]">{money(tuitionClaimed, "USD")}</div>
        </div>
      )}

      {caps.map((c, i) => {
        // Over (including rent in a below-half-time term next to a term with
        // room, ZEN-809) always shows a full red bar.
        const pct = c.over ? 100 : c.cap > 0 ? Math.min(100, (c.claimed / c.cap) * 100) : 0;
        const barColor = c.over ? "#F01919" : pct > 85 ? "#F68F51" : "#077A39";
        return (
          <div key={i} className={`mb-3 rounded-lg border p-3 ${c.over ? "border-[#F01919] bg-[#F01919]/5" : "border-[#C2C9D1]"}`} data-testid={`coa-cap-${i}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold">{c.bucket}</span>
              {c.over && <span className="rounded-full bg-[#F01919] px-2 py-0.5 text-[10px] font-bold text-white">Over cap</span>}
            </div>
            <div className="mt-0.5 text-[11px] text-[#6b7280]">{c.school}</div>
            {(c.cap > 0 || c.over) && (
              <>
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-[#E9E9EB]">
                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: barColor }} />
                </div>
                <div className="mt-1.5 flex items-center justify-between text-xs">
                  <span className="tnum font-semibold">{money(c.claimed, "USD")} <span className="text-[#6b7280]">of {money(c.cap, "USD")}</span></span>
                  <span className={`tnum font-bold ${c.over ? "text-[#F01919]" : "text-[#1f2937]"}`}>{c.over ? `${money(Math.abs(c.remaining), "USD")} over` : `${money(c.remaining, "USD")} left`}</span>
                </div>
              </>
            )}
            {c.over && (
              <div className="mt-2 rounded-md border border-[#F01919]/30 bg-[#F01919]/5 px-3 py-2 text-xs text-[#991B1B]" data-testid="rb-over-cap-alert">
                {RB_OVER_MSG}
              </div>
            )}
            {(() => {
              // ZEN-818 situations B and C: who paid the rent and food at this
              // school, the part outside the 529 lumped into one figure.
              const p = (payers || []).find((x) => x.enrollment_id === c.enrollment_id);
              if (!p) return null;
              return (
                <div className="mt-2 space-y-0.5 text-xs" data-testid={`rb-payers-${i}`}>
                  <div className="flex justify-between"><span className="text-[#374151]">{PAID_BY_529_LABEL}</span><span className="tnum">{money(p.from_529, "USD")}</span></div>
                  {p.outside > 0.005 && (
                    <div className="flex justify-between" data-testid={`rb-paid-outside-${i}`}>
                      <span className="text-[#374151]">{PAID_OUTSIDE_LABEL}{p.outside_has_private_settlement && <span className="text-[#6b7280]"> · {SEE_PRIVATE_SETTLEMENTS}</span>}</span>
                      <span className="tnum">{money(p.outside, "USD")}</span>
                    </div>
                  )}
                </div>
              );
            })()}
            {(c.periods || []).length > 0 && (
              <div className="mt-2 space-y-1">
                <div className="text-[10px] font-bold uppercase text-[#6b7280]">Period breakdown</div>
                {c.periods.map((pd, j) => (
                  <div key={j} className={`flex flex-col gap-0.5 rounded px-2 py-1.5 text-[11px] ${pd.caution ? "bg-[#FCF5EE] border border-[#D97706]/30" : "bg-[#F8FAFC]"}`} data-testid={`coa-period-${i}-${j}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-[#374151]">{pd.label}{pd.housing_type === "school_housing" && <span className="ml-1 rounded bg-[#034AA2]/10 px-1.5 py-0.5 text-[9px] font-semibold text-[#034AA2]">School housing</span>}</span>
                      <span className="flex items-center gap-2 tnum text-[#6b7280]">
                        {pd.amount_pending ? (
                          <span className="italic text-[#6b7280]" data-testid={`coa-period-not-entered-${i}-${j}`}>Not entered yet</span>
                        ) : pd.rate_line ? (
                          <span data-testid={`coa-period-rate-wait-${i}-${j}`}>{pd.rate_line}</span>
                        ) : pd.half_time ? (
                          <>{pd.months_in_year}mo × {money(pd.monthly_cap, "USD")} = {money(pd.coa_allowance || pd.cap_contribution, "USD")}{pd.housing_type === "school_housing" && pd.cap_contribution > (pd.coa_allowance || 0) + 0.005 ? <span className="ml-1 text-[#034AA2]">→ {money(pd.cap_contribution, "USD")} (invoiced)</span> : ""}</>
                        ) : (
                          <span className="italic text-[#9CA3AF]">Not half-time — $0</span>
                        )}
                      </span>
                    </div>
                    {pd.period_claimed > 0 && (
                      <div className="text-[10px] text-[#6b7280]">Tagged claimed: {money(pd.period_claimed, "USD")}{pd.amount_pending || pd.rate_line ? "" : <> of {money(pd.period_allowance, "USD")}</>}</div>
                    )}
                    {pd.invoice_hint && (
                      <div className="mt-0.5 text-[10px] text-[#D97706]" data-testid={`coa-period-invoice-hint-${i}-${j}`}>
                        {pd.invoice_hint}
                      </div>
                    )}
                    {pd.housing_type === "school_housing" && pd.invoice_usd > 0 && pd.headroom_beyond_invoice != null && (
                      <div className="mt-0.5 text-[10px]" data-testid={`coa-period-headroom-${i}-${j}`}>
                        <span className={pd.headroom_beyond_invoice > 0 && !(pd.period_claimed > pd.period_allowance + 0.005) ? "text-[#077A39]" : "text-[#374151]"}>
                          {pd.headroom_beyond_invoice > 0 ? (
                            pd.period_claimed > pd.invoice_usd + 0.005
                              ? <>Beyond the school&apos;s charges, {money(Math.max(0, pd.period_allowance - pd.period_claimed), "USD")} of that {money(pd.headroom_beyond_invoice, "USD")} remains for additional room &amp; board (e.g., groceries) this term</>
                              : <>Beyond the school&apos;s charges, up to {money(pd.headroom_beyond_invoice, "USD")} of additional room &amp; board (e.g., groceries) can qualify this term</>
                          ) : (
                            <>School invoice matches the cap — no additional room &amp; board headroom this term</>
                          )}
                        </span>
                        {" "}<InfoTip testid={`coa-period-headroom-tip-${i}-${j}`} size={11}>Cap = the greater of your school&apos;s COA allowance or the actual school invoice (IRS Pub 970); spending beyond both is not qualified.</InfoTip>
                      </div>
                    )}
                    {pd.caution && (
                      <div className="mt-0.5 text-[10px] text-[#92400E]" data-testid={`coa-period-caution-${i}-${j}`}>
                        Room &amp; board recorded for this period exceeds {pd.housing_type === "school_housing" ? "both the COA allowance and the school-invoiced amount" : "its COA allowance"} — but your tax-year total is still within the combined annual cap.
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </Card>
  );
}

function WithdrawalTile({ year, withdrawn, withdrawalCount, reconciledTotal, reconciledCount, testid }) {
  const incomplete = withdrawalCount > 0 && reconciledCount < withdrawalCount;
  return (
    <Card className="fade-in" data-testid={testid}>
      <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#6b7280]">
        <Bank size={14} weight="bold" className="shrink-0" />
        <span className="truncate">{`529 Distributions · ${year}`}</span>
      </span>
      <div className="mt-2 text-2xl font-extrabold tnum text-[#1f2937]">{money(withdrawn, "USD")}</div>
      <div className="mt-1 flex flex-wrap items-center gap-1 text-xs font-medium text-[#6b7280]">
        <span>To reconcile with 1099-Q</span>
        <InfoTip text="Form 1099-Q reports these distributions. Your 529 plan administrator must send it to you by January 31 of the year after the distribution — you'll reconcile this amount against it then." testid={testid ? `${testid}-1099q-info` : undefined} />
      </div>
      {withdrawalCount > 0 && (
        <div className={`mt-2 flex flex-wrap items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold ${incomplete ? "border border-[#D97706]/30 bg-[#FCF5EE] text-[#92600A]" : "text-[#1f2937]"}`}
          data-testid={testid ? `${testid}-reconciled` : undefined}>
          <span className="tnum">{money(reconciledTotal, "USD")}</span>
          <span>·</span>
          <span>{reconciledCount} of {withdrawalCount}</span>
        <InfoTip text="Each 529 distribution appears here once its Date Received is entered. Fill in any missing Date Received (the field marked in amber) and that distribution will be added — until the total matches the distributions above." testid={testid ? `${testid}-reconciled-info` : undefined} />
        </div>
      )}
    </Card>
  );
}

export function SafeToWithdrawCard({ aqee, rec, canEditSchools = false, year, nav, studentId }) {
  const coming = useComingUp(studentId);
  if (!aqee) return null;
  const stw = aqee.safe_to_withdraw ?? 0;
  // AHEAD OF COSTS, NOT BEYOND THEM: the one shared split (ZEN-804, ZEN-806).
  // Paid costs waiting for a final exchange rate are amber too (ZEN-835).
  const { kind, over: overBy, awaiting: awaitingBy, ahead: aheadBy, beyond: beyondBy } = withdrawalsHeadline(aqee);
  const isOverWithdrawn = overBy > 0.005;
  const isPositive = stw > 0.005;
  const isBeyond = kind === "beyond";
  const isAhead = kind === "ahead";
  const isAmber = kind === "ahead" || kind === "awaiting";
  const awaitingLine = awaitingBy > 0.005 && (
    <div className="mt-1 text-sm text-[#92400E]" data-testid="safe-to-withdraw-awaiting">
      {kind !== "awaiting" && <>{money(awaitingBy, "USD")} {headlineWords("awaiting", year)}. </>}{AWAITING_RATE_SENTENCE}
    </div>
  );
  // The math under an amber or red card (ZEN-807, Maryse 26 Oct 2026).
  const L = yearLines(aqee);
  const addExpense = () => nav(`/expenses?student_id=${studentId}&tab=529&tax_year=${year}`);

  // Derivation line items for a card with nothing over (unchanged).
  const items = [];
  if (aqee.total_qualified > 0) items.push({ label: "Qualified expenses", val: aqee.total_qualified, to: null });
  if (aqee.tax_free_assistance > 0) items.push({ label: "Scholarships", val: aqee.tax_free_assistance, neg: true, to: "/scholarships" });
  if (aqee.aotc_llc_reserved > 0) items.push({ label: "AOTC reserve", val: aqee.aotc_llc_reserved, neg: true, to: "/expenses" });
  if ((aqee.total_withdrawn ?? 0) > 0) items.push({ label: "Already withdrawn", val: aqee.total_withdrawn, neg: true, to: "/withdrawals" });

  return (
    <Card className={`fade-in ${isAmber ? "!border-[#D97706]/40 !bg-[#FFFBEB]" : isOverWithdrawn ? "!border-[#F01919]/30 !bg-[#FFF5F5]" : isPositive ? "!border-[#077A39]/20 !bg-[#F0FFF4]" : ""}`} data-testid="safe-to-withdraw-card">
      {/* ONE CARD, "529 WITHDRAWALS · <year>" (Maryse, 26 Oct 2026, ZEN-814
          point 3): the year on top, the next step below. The headline is the
          same size as the sentence under it — bold, and its colour does the
          signalling. */}
      <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#6b7280]" data-testid="withdrawals-card-heading">
        <ShieldCheck size={14} weight="bold" />
        529 Withdrawals · {year}
      </div>
      <div className={`mt-2 text-sm font-bold tnum ${isAmber ? "text-[#B45309]" : isOverWithdrawn ? "text-[#F01919]" : isPositive ? "text-[#077A39]" : "text-[#1f2937]"}`} data-testid="safe-to-withdraw-value">
        {kind !== "safe" ? (
          <>{money(kind === "beyond" ? beyondBy : kind === "ahead" ? aheadBy : awaitingBy, "USD")} {headlineWords(kind, year)}</>
        ) : (
          money(stw, "USD")
        )}
      </div>
      {isAhead && (
        <div className="mt-1 text-sm text-[#92400E]" data-testid="safe-to-withdraw-ahead">
          Covered by costs still to come in {year}. Nothing to do, as long as they&apos;re paid by December 31.{" "}
          {coming.plan && !(coming.comingNext > 0.005) && (
            <button onClick={() => tieUntiedNav(nav, coming.plan)} className="font-semibold underline hover:text-[#78350F]" data-testid="safe-to-withdraw-tie">Tie it to expense(s) →</button>
          )}
        </div>
      )}
      {isAmber && awaitingLine}
      {isBeyond && (
        <div className="mt-1 text-sm text-[#991B1B]" data-testid="safe-to-withdraw-beyond">
          {aheadBy > 0.005
            ? <>Costs still to come in {year} cover the other {money(aheadBy, "USD")}.</>
            : awaitingBy > 0.005 ? null : <>No {year} costs are left to cover it.</>}
          {" "}If you have {year} qualified costs you haven&apos;t recorded yet, add them. Otherwise, the earnings in this amount may be taxable.{" "}
          <button onClick={addExpense} className="font-semibold text-[#F01919] hover:underline" data-testid="safe-to-withdraw-add-expense">Add an expense →</button>
        </div>
      )}
      {isBeyond && awaitingLine}
      {isOverWithdrawn && hasStillToCome(aqee) ? (
        <div className="mt-2 space-y-0.5 text-[11px] text-[#6b7280] tnum" data-testid="safe-to-withdraw-lines">
          <MathLine op="" label="Qualified costs already paid" val={L.paid} />
          {L.awaitingRate > 0.005 && <MathLine op="+" label="Qualified costs waiting for a final exchange rate" val={L.awaitingRate} />}
          <MathLine op="+" label={`Qualified costs still to come in ${year}`} val={L.toCome} />
          <MathLine op="=" label={`Qualified costs for ${year}`} val={L.yearTotal} strong />
          {L.aid > 0.005 && <MathLine op="−" label="Scholarships" val={L.aid} onClick={() => nav("/scholarships")} />}
          {L.reserve > 0.005 && <MathLine op="−" label="AOTC reserve" val={L.reserve} onClick={() => nav("/expenses")} />}
          <MathLine op="−" label="Already withdrawn" val={L.withdrawn} onClick={() => nav("/withdrawals")} />
          {isBeyond
            ? <MathLine op="=" label="Over by" val={beyondBy} strong />
            : <MathLine op="=" label={`Still safe to withdraw in ${year}`} val={Math.max(0, L.result)} strong />}
        </div>
      ) : items.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-[#6b7280]" data-testid="safe-to-withdraw-derivation">
          {items.map((it, idx) => (
            <span key={idx} className="inline-flex items-center gap-0.5">
              {idx > 0 && <span className="text-[#9aa1a9]">{it.neg ? "−" : "+"}</span>}
              {it.to ? (
                <button onClick={() => nav(it.to)} className="font-semibold hover:underline">{it.label} {money(it.val, "USD")}</button>
              ) : (
                <span className="font-semibold">{it.label} {money(it.val, "USD")}</span>
              )}
            </span>
          ))}
        </div>
      )}
      {/* ZEN-809: how rent and food were counted, in words. */}
      {(rec?.room_and_board_above_allowance ?? 0) > 0.005 && (
        <div className="mt-1.5 text-[11px] text-[#374151]" data-testid="safe-to-withdraw-rb-above">
          Rent and food count only up to the school&apos;s room-and-board allowance. {money(rec.room_and_board_above_allowance, "USD")} above it is not counted as qualified.
        </div>
      )}
      {(rec?.room_and_board_unchecked ?? 0) > 0.005 && (
        <div className="mt-1.5 text-[11px] text-[#92600A]" data-testid="safe-to-withdraw-rb-unchecked">
          We can&apos;t check {money(rec.room_and_board_unchecked, "USD")} of rent and food against a school allowance, so it is counted in full.{" "}
          {canEditSchools && <button onClick={() => nav("/students")} className="font-semibold underline hover:text-[#78350F]" data-testid="safe-to-withdraw-rb-unchecked-link">Check the school&apos;s room-and-board allowance →</button>}
        </div>
      )}
      <div className="mt-1 text-[10px] italic text-[#9CA3AF]">Based on what you&apos;ve recorded</div>
      <NextStepSection coming={coming} nav={nav} onWithdraw={() => nav("/withdrawals")} />
    </Card>
  );
}


/** One line of the card's math: operator, label, amount. */
function MathLine({ op, label, val, strong, onClick }) {
  const text = <>{label} {money(val, "USD")}</>;
  return (
    <div className={`flex gap-1.5 ${strong ? "font-semibold text-[#374151]" : ""}`}>
      <span className="w-3 text-center text-[#9aa1a9]">{op}</span>
      {onClick ? <button onClick={onClick} className="text-left hover:underline">{text}</button> : <span>{text}</span>}
    </div>
  );
}

/** HOW THE 529 COSTS WERE PAID (ZEN-814; Maryse 26 and 28 Oct 2026). The rows
 *  and their words come from the server (_paid_by_rows), and they add up to
 *  "Qualified costs already paid" — the same figure the withdrawals card
 *  starts from. Replaces the three loose totals that sat on the page. */
export function PaidByCard({ paidBy }) {
  const row = (label, amount, testid, extra = "") => (
    <div className={`flex items-baseline justify-between gap-3 py-0.5 ${extra}`} data-testid={testid}>
      <span>{label}</span><span className="tnum shrink-0">{amount}</span>
    </div>
  );
  return (
    <Card data-testid="paid-by-card">
      <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-[#6b7280]">How the 529 costs were paid</h3>
      <div className="text-sm text-[#1f2937]">
        {paidBy.rows.map((r) => row(r.label, money(r.amount, "USD"), `paid-by-${r.kind}-${r.payor_ref}`))}
        {paidBy.above_allowance > 0.005 && row(`− ${paidBy.above_allowance_label}`, money(paidBy.above_allowance, "USD"), "paid-by-above-allowance")}
        {row(paidBy.total_label, money(paidBy.total, "USD"), "paid-by-total", "mt-1 border-t border-[#C2C9D1] pt-1.5 font-semibold")}
      </div>
    </Card>
  );
}

/** What the child paid from their own money, and what others paid for them
 *  that nobody will pay back (ZEN-814 point 4 and Q5, Maryse 26 and 28 Oct
 *  2026). Family figures only — never in the 529 part or the ledger PDF. */
export function OwnMoneyCard({ name, year, own, others }) {
  return (
    <Card data-testid="own-money-card">
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="font-semibold text-[#1f2937]" data-testid="own-money-title">{ownMoneyTitle(name, year)}</span>
        <span className="tnum font-semibold" data-testid="own-money-total">{money(own || 0, "USD")}</span>
      </div>
      <div className="mt-3 text-xs font-bold uppercase tracking-wide text-[#6b7280]" data-testid="others-paid-title">
        Paid for {name || "this beneficiary"} by others, no reimbursement expected
      </div>
      {others.length === 0 ? (
        <div className="mt-1 text-sm text-[#6b7280]" data-testid="others-paid-none">None</div>
      ) : (
        <div className="mt-1 text-sm">
          {others.map((c) => (
            <div key={c.payor_ref} className="flex items-baseline justify-between gap-3 py-0.5" data-testid={`others-paid-${c.payor_ref}`}>
              <span>{c.payor_nick}</span><span className="tnum">{money(c.total, "USD")}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// Approved 28 Oct 2026 (ZEN-814): "Paid from Sophia's own money · 2026" — no
// "her" or "his", since the app does not record whether a child is a boy or a girl.
export function ownMoneyTitle(name, year) {
  return `Paid from ${name ? `${name}'s` : "this beneficiary's"} own money · ${year}`;
}

/** The Family Money Tools PDF, in the band's header (ZEN-814 point 5): the
 *  year chosen on the page (ZEN-813). */
function FamilyMoneyPdfBtn({ studentId, year }) {
  const [busy, setBusy] = useState(false);
  const dl = async () => {
    setBusy(true);
    try {
      await downloadFile("/reports/family-contributions", `family-money-tools-${year}.pdf`, { student_id: studentId, year });
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <button onClick={dl} disabled={busy} data-testid="family-contrib-download"
      className="flex shrink-0 items-center gap-1 text-xs font-semibold text-[#034AA2] hover:underline disabled:opacity-50"
      title="Download Family Money Tools PDF">
      <FilePdf size={14} weight="bold" />
      {busy ? "Downloading…" : "Download PDF"}
    </button>
  );
}
function Skeleton() {
  return <div className="grid grid-cols-1 gap-4 @sm:grid-cols-2 @3xl:grid-cols-4">{[0, 1, 2, 3].map((i) => <div key={i} className="h-28 animate-pulse rounded-xl border border-[#C2C9D1] bg-white" />)}</div>;
}

function LoadError({ onRetry }) {
  return (
    <Card className="flex flex-col items-center gap-3 py-12 text-center" data-testid="dashboard-load-error">
      <Warning size={32} weight="bold" className="text-[#F01919]" />
      <div>
        <div className="font-bold text-[#1f2937]">Couldn&apos;t load Overview data</div>
        <div className="mt-1 text-sm text-[#6b7280]">The request didn&apos;t complete. Check your connection and try again.</div>
      </div>
      <Btn onClick={() => onRetry?.()} data-testid="dashboard-retry-btn"><ClockCountdown size={16} weight="bold" /> Retry</Btn>
    </Card>
  );
}


/**
 * The card's lines. With All beneficiaries picked, a warning about several
 * people becomes one line per person, each opening on that person — a single
 * button cannot land on two people at once. (ZEN-768) The server builds every
 * address; this only chooses which one each line uses.
 */
export function attentionLines(sources, studentId) {
  const lines = [];
  // A source may carry its own lines, one per year (ZEN-782).
  for (const s of sources.flatMap((x) => x.lines || [x]).filter((x) => x.count > 0)) {
    const people = (s.students || []).filter((st) => st.count > 0);
    // Split only when every line can name its person. A record with no
    // beneficiary has no person to open on, so its warning stays one line
    // whose button opens everything that warning counted.
    if (!studentId && people.length > 1 && people.every((st) => st.student_id)) {
      for (const st of people) {
        lines.push({ id: `${s.key}-${st.student_id || "none"}`, label: s.label, count: st.count,
          names: st.name, link: st.link || s.link, action: s.action });
      }
    } else {
      lines.push({ id: s.key, label: s.label, count: s.count,
        names: studentId ? "" : people.map((st) => st.name).join(", "), link: s.link, action: s.action });
    }
  }
  return lines;
}

// The Overview's expense status, as TWO cards that never share a table: one in
// the 529 part of the page with only 529-eligible counts, one beside the family
// money tools with only Non-529 counts (Maryse, 28 Oct 2026, ZEN-811). The
// expenses waiting to be classified are in neither card's rows; both cards show
// the same To classify line for them.
//
// Which rows a Non-529 card needs, traced in the server code 28 Oct 2026:
// Scheduled (any future-dated expense), Awaiting (paid back privately, until a
// private settlement closes it), Closed and Split. In progress and Reconciled
// come only from matching a 529 withdrawal, which refuses a Non-529 expense —
// so those two rows show on the Non-529 card only when something is in them,
// and an odd old record is never hidden behind a missing row.
const STATUS_CARDS = {
  "529": { key: "eligible_529", title: "Expense status · 529-eligible", testid: "status-card-529",
    always: ["scheduled", "confirmed", "in_progress", "reconciled", "closed", "split"] },
  "non529": { key: "non_529", title: "Expense status · Non-529", testid: "status-card-non529",
    always: ["scheduled", "confirmed", "closed", "split"] },
};

function ToClassifyLine({ toClassify, nav, testid }) {
  const waiting = toClassify?.count || 0;
  // Always shown; amber only when there is something to do (Maryse, 27 Oct 2026).
  return (
    <div className={`mb-3 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border px-3 py-2 ${waiting > 0 ? "border-[#D97706]/30 bg-[#FEF3C7]" : "border-transparent"}`}
      data-testid={`${testid}-to-classify`} data-amber={waiting > 0 ? "yes" : "no"}>
      <StatusChip status="draft" />
      <span className={`text-xs font-semibold ${waiting > 0 ? "text-[#92400E]" : "text-[#374151]"}`}>
        {waiting === 1 ? "1 expense needs to be classified" : `${waiting} expenses need to be classified`}
      </span>
      {waiting > 0 && (
        <a href={toClassify.link} onClick={(ev) => { ev.preventDefault(); nav(toClassify.link); }}
          className="ml-auto whitespace-nowrap text-xs font-semibold text-[#034AA2] hover:underline" data-testid={`${testid}-classify-link`}>
          {waiting === 1 ? "Classify it →" : "Classify them →"}
        </a>
      )}
    </div>
  );
}

export function StatusCard({ data, kind, isOwner, nav }) {
  const s = data.status_summary;
  const card = STATUS_CARDS[kind];
  if (!s || !card) return null;
  const counts = s[card.key] || {};
  const rows = Object.keys(STATUS_META).filter((k) => card.always.includes(k) || (counts[k] || 0) > 0);
  return (
    <Card data-testid={card.testid}>
      <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-[#6b7280]" data-testid={`${card.testid}-title`}>{card.title}</h3>
      {isOwner && <ToClassifyLine toClassify={s.to_classify} nav={nav} testid={card.testid} />}
      <div className="space-y-2">
        {rows.map((k) => (
          <button key={k} onClick={() => nav(`/expenses`)} className="flex w-full items-center justify-between gap-2 rounded-md border border-[#C2C9D1] px-3 py-2 text-left hover:bg-[#E9E9EB]" data-testid={`${card.testid}-row-${k}`}>
            <StatusChip status={k} />
            <span className="tnum font-bold" data-testid={`${card.testid}-count-${k}`}>{counts[k] || 0}</span>
          </button>
        ))}
      </div>
    </Card>
  );
}

export function AttentionCard({ studentId }) {
  const nav = useNavigate();
  const url = studentId ? `/attention?student_id=${studentId}` : "/attention";
  const { data: attention, isLoading } = useQuery({
    queryKey: ["attention", studentId || "all"],
    queryFn: async () => (await api.get(url)).data,
    staleTime: 30000,
    refetchOnWindowFocus: true,
  });
  if (isLoading || !attention) return null;

  if (attention.total === 0) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-[#077A39]/20 bg-[#077A39]/5 px-4 py-3 text-sm font-semibold text-[#077A39]" data-testid="attention-all-clear">
        <CheckCircle size={18} weight="fill" />
        All caught up — nothing needs your attention.
      </div>
    );
  }

  return (
    <Card data-testid="attention-card">
      <div className="flex items-center gap-2 mb-3">
        <BellSimple size={18} weight="fill" className="text-[#D97706]" />
        <h3 className="text-sm font-bold uppercase tracking-wide text-[#D97706]">Needs your attention</h3>
      </div>
      <div className="space-y-2">
        {attentionLines(attention.sources, studentId).map((line) => (
          <div key={line.id} className="flex items-center justify-between gap-3 rounded-lg border border-[#D97706]/20 bg-[#FCF5EE] px-3 py-2.5" data-testid={`attention-row-${line.id}`}>
            <div className="flex items-center gap-2 text-sm text-[#92600A]">
              <span className="flex h-6 min-w-[24px] items-center justify-center rounded-full bg-[#D97706] px-1.5 text-[10px] font-bold text-white">{line.count}</span>
              <span>{line.label}{line.names ? ` — ${line.names}` : ""}</span>
            </div>
            <button onClick={() => nav(line.link)} className="shrink-0 rounded-md bg-[#034AA2] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#023580]" data-testid={`attention-review-${line.id}`}>
              {line.action || "Review now"}
            </button>
          </div>
        ))}
      </div>
    </Card>
  );
}


/**
 * The address of the field that fixes a missing expected end date: the
 * Beneficiary(ies) page, with that beneficiary's edit dialog open and the
 * expected-completion-date field focused and ringed.
 *
 * Students.jsx is the OTHER HALF of this one path — it reads both parameters
 * (the ?edit/?focus effect near the top of that file, and StudentDialog's own
 * focus effect). Neither half is a feature alone, which is exactly how they
 * came apart: the projections redesign of 2026-07-23 removed the chip that
 * produced this URL and left the receiving code sitting there, referenced by
 * nothing, for five weeks. The Overview went on telling the account owner a
 * date was missing while offering no way to go and add it (ZEN-22). Naming the
 * URL here rather than inlining it gives that receiving code something
 * findable to point back at.
 *
 * DELIBERATELY NOT paired with selectStudent(). The Beneficiary(ies) page lists
 * everyone the account owner can see and ignores the top-bar selection, so the
 * dialog opens on the right person without it. Changing the app-wide selection
 * as a side effect of a fix-it link would silently re-scope every other page —
 * and it persists to local storage, so it would outlast the visit.
 */
/**
 * The link from the "could not be converted into US dollars" note to the
 * expenses that caused it.
 *
 * ★ EVERY DIMENSION THE COUNT WAS COMPUTED UNDER HAS TO TRAVEL WITH IT.
 *
 * The count beside this link is scoped to a beneficiary AND a tax year. The
 * expense list is scoped the same way and opens on its own defaults. So any
 * dimension the link forgets is a dimension on which the list quietly shows a
 * DIFFERENT set than the one that was counted — the owner is told a number and
 * then shown rows that do not add up to it, or none at all.
 *
 * That has now happened twice, found by review both times and never by me: the
 * first version forgot the eligibility tab, the second forgot the tax year. Two
 * dimensions, one mistake, made twice — which is why this is a function with
 * tests rather than a string assembled at the point of use. A string has to
 * REMEMBER; a tested function has to be CHANGED, and the test says what it owes.
 *
 * Same shape and same reason as endDateFixLink below: a warning that cannot be
 * acted on teaches people to ignore warnings. (ZEN-632)
 */
export function unpricedExpensesLink(studentId, year) {
  const p = new URLSearchParams();
  if (studentId) p.set("student_id", studentId);
  // A dashboard with no year chosen must not send "null" as one — the list
  // would scope to a year that does not exist and show nothing at all.
  if (year) p.set("tax_year", String(year));
  p.set("needs_rate", "1");
  return `/expenses?${p.toString()}`;
}

export function endDateFixLink(studentId) {
  return `/students?edit=${encodeURIComponent(studentId)}&focus=end-date`;
}

/**
 * The beneficiaries the 529 projection had to leave out because no active
 * enrolment carries an expected end date, and the one-click path to add it.
 *
 * A warning that cannot be acted on teaches people to ignore warnings, and this
 * app asks the account owner to act on several. So the names ARE the fix: one
 * click lands on that beneficiary with the date field focused.
 *
 * Amber band, blue names: amber is this app's "pay attention" colour and the
 * band keeps it, while blue is its "you can click this" colour and marks the
 * only part of the sentence that does something. The underline is permanent
 * rather than hover-only — a name at the size of the surrounding text has no
 * other affordance, and the notice is worthless if the link is not noticed.
 *
 * Exported and kept out of ProjectionsRollupCard so the link can be tested
 * without standing up the projection queries it lives beside (ZEN-22).
 */
export function ProjectionExclusionNotice({ students, onFix }) {
  const one = students.length === 1;
  return (
    <div className="mb-3 flex items-start gap-2 rounded-md bg-[#D97706]/10 px-3 py-2" data-testid="projections-exclusion-notice">
      <Warning size={16} weight="bold" className="mt-0.5 shrink-0 text-[#D97706]" />
      <span className="text-xs font-semibold text-[#D97706]">
        {students.map((s, i) => (
          <React.Fragment key={s.id}>
            {i > 0 ? ", " : null}
            <button
              type="button"
              onClick={() => onFix(s.id)}
              aria-label={`Add ${s.full_name}'s expected end date`}
              data-testid={`projections-add-end-date-${s.id}`}
              className="rounded-sm font-semibold text-[#034AA2] underline underline-offset-2 hover:text-[#023580] focus:outline-none focus:ring-2 focus:ring-[#034AA2] focus:ring-offset-1"
            >
              {s.full_name}
            </button>
          </React.Fragment>
        ))}{" "}
        {one ? "needs" : "need"} an expected end date. Select {one ? "the name" : "a name"} to add it under Beneficiary(ies) and see {one ? "their" : "each"} projection.
      </span>
    </div>
  );
}

function ProjectionsRollupCard({ students, nav }) {
  const { selectStudent } = useAuth();
  // Fetch projection inputs for all students in parallel
  const results = useQueries({
    queries: students.map((s) => ({
      queryKey: ["projection-inputs", s.id],
      queryFn: async () => (await api.get(`/projections/inputs?student_id=${s.id}`)).data,
      enabled: !!s.id,
      staleTime: 60_000,
    })),
  });

  const allLoaded = results.every((r) => !r.isLoading);
  if (!allLoaded) return null;

  // Build projection rows — only for higher-ed (post_secondary) beneficiaries
  const rows = [];
  const failedIndices = [];
  results.forEach((r, i) => {
    const stu = students[i];
    // Scope: only include students with at least one active higher-ed enrollment
    const hasHigherEdEnrollment = (stu.schools || []).some(sc => !sc.archived && (sc.expense_group || "post_secondary") === "post_secondary");
    if (!hasHigherEdEnrollment) return;
    if (r.isError || !r.data) {
      failedIndices.push(i);
      return;
    }
    const inp = r.data;
    const proj = runProjection({
      currentBalance: inp.current_balance || 0,
      monthsRemaining: inp.months_remaining,
      annualCost: inp.expected_annual_cost || 0,
      annualContribution: inp.recent_annual_contributions || 0,
      annualGrowthRate: 0.03,
      pauses: [],
    });
    const totalCost = (inp.expected_annual_cost || 0) * ((inp.months_remaining || 0) / 12);
    // Coverage: if surplus, 100%; if shortfall, use endBalance relative to totalCost
    let pctCoverage;
    if (proj.verdict === "shortfall") {
      // Shortfall: how much of the total cost could the plan cover?
      const covered = totalCost + proj.endBalance; // endBalance is negative
      pctCoverage = totalCost > 0 ? Math.max(0, Math.round((covered / totalCost) * 100)) : 0;
    } else {
      pctCoverage = 100;
    }
    rows.push({
      student: stu,
      balance: inp.current_balance || 0,
      monthsRemaining: inp.months_remaining,
      totalCostRemaining: totalCost,
      verdict: proj.verdict,
      endBalance: proj.endBalance,
      shortfallMonth: proj.shortfallMonth,
      pctCoverage,
      // false = some 529 account has no balance anchored, so current_balance is
      // a subtotal and a "shortfall" verdict below may not be real.
      balanceComplete: inp.balance_complete !== false,
    });
  });

  // Check if any higher-ed students exist at all
  const higherEdStudents = students.filter((s) => (s.schools || []).some(sc => !sc.archived && (sc.expense_group || "post_secondary") === "post_secondary"));
  const hasHigherEd = higherEdStudents.length > 0;

  // If no higher-ed students, show a one-line pointer to caps panel
  if (!hasHigherEd) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-[#C2C9D1] bg-white px-4 py-3 text-sm text-[#6b7280]" data-testid="projections-no-higher-ed">
        <ChartLineUp size={16} weight="bold" className="shrink-0 text-[#034AA2]" />
        <span>Using your 529 for K-12 or other withdrawals? <button onClick={() => document.querySelector('[data-testid="coa-caps-card"]')?.scrollIntoView({ behavior: "smooth" })} className="font-semibold text-[#034AA2] hover:underline">See the caps panel &rarr;</button></span>
      </div>
    );
  }

  // Filter out no_data and complete from visible cards
  const visibleRows = rows.filter((r) => r.verdict !== "no_data" && r.verdict !== "complete");
  const noDataRows = rows.filter((r) => r.verdict === "no_data");

  if (!visibleRows.length && !failedIndices.length && !noDataRows.length) return null;

  const retryFailed = () => {
    for (const idx of failedIndices) {
      results[idx].refetch();
    }
  };

  return (
    <div data-testid="projections-rollup-card">
      <div className="flex items-center gap-2 mb-2">
        <ChartLineUp size={20} weight="bold" className="text-[#034AA2]" />
        <div>
          <h3 className="text-sm font-extrabold uppercase tracking-wide text-[#0A0A0A]">529 account projection</h3>
          <p className="text-xs text-[#6b7280]">Will each beneficiary&apos;s 529 account cover their higher education?</p>
        </div>
      </div>

      {/* Failed requests notice */}
      {failedIndices.length > 0 && (
        <div className="mb-3 flex items-start gap-2 rounded-md bg-[#D97706]/10 px-3 py-2" data-testid="projections-error-notice">
          <Warning size={16} weight="bold" className="mt-0.5 shrink-0 text-[#D97706]" />
          <span className="text-xs font-semibold text-[#D97706]">
            Could not load projection data for {failedIndices.length} beneficiar{failedIndices.length === 1 ? "y" : "ies"}.
          </span>
          <button onClick={retryFailed} className="ml-auto shrink-0 text-xs font-semibold text-[#034AA2] hover:underline" data-testid="projections-retry-btn">
            <ArrowsClockwise size={12} weight="bold" className="inline mr-0.5" />Retry
          </button>
        </div>
      )}

      {/* Incomplete-balance notice: a projection computed from a partial
          balance can show a shortfall that is not real. Name who is affected
          rather than silently presenting the verdict as trustworthy. */}
      {visibleRows.some((r) => !r.balanceComplete) && (
        <div className="mb-3 flex items-start gap-2 rounded-md bg-[#D97706]/10 px-3 py-2" data-testid="projections-incomplete-balance-notice">
          <Warning size={16} weight="bold" className="mt-0.5 shrink-0 text-[#D97706]" />
          <span className="text-xs font-semibold text-[#D97706]">
            {visibleRows.filter((r) => !r.balanceComplete).map((r) => r.student.full_name).join(", ")}{" "}
            {visibleRows.filter((r) => !r.balanceComplete).length === 1 ? "has" : "have"} a 529 account with no
            balance entered, so {visibleRows.filter((r) => !r.balanceComplete).length === 1 ? "this" : "these"} projection
            {visibleRows.filter((r) => !r.balanceComplete).length === 1 ? " runs" : "s run"} on a partial balance.
            Add the balance in Accounts &amp; Payers.
          </span>
        </div>
      )}

      {/* No-data notice — every name is the way to fix it. See ProjectionExclusionNotice. */}
      {noDataRows.length > 0 && (
        <ProjectionExclusionNotice
          students={noDataRows.map((r) => r.student)}
          onFix={(id) => nav(endDateFixLink(id))}
        />
      )}

      {/* Per-student isolated cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {visibleRows.map((r) => {
          const isSurplus = r.verdict === "surplus";
          const isShortfall = r.verdict === "shortfall";
          const isTight = r.verdict === "tight";
          const shortBy = isShortfall ? Math.abs(r.endBalance) : 0;
          const surplus = isSurplus ? r.endBalance : 0;
          return (
            <Card key={r.student.id} className={`${isShortfall ? "border-[#D97706]" : ""}`} data-testid={`projections-card-${r.student.id}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="font-bold text-sm text-[#0A0A0A] truncate">{r.student.full_name}</span>
                <InfoTip testid={`projections-info-${r.student.id}`} size={13}>
                  Status compares {r.student.full_name}&apos;s 529 balance and expected annual cost against the expected end date of their current school.
                </InfoTip>
              </div>
              {/* Status line */}
              <div className="mb-3">
                {isSurplus && (
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center rounded-full bg-[#077A39]/10 px-2.5 py-0.5 text-xs font-semibold text-[#077A39]" data-testid={`verdict-surplus-${r.student.id}`}>On track</span>
                    <span className="text-xs font-semibold tnum text-[#077A39]">+{money(surplus, "USD", { accounting: true })} surplus</span>
                  </div>
                )}
                {isTight && (
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center rounded-full bg-[#D97706]/10 px-2.5 py-0.5 text-xs font-semibold text-[#D97706]" data-testid={`verdict-tight-${r.student.id}`}>Tight</span>
                    <span className="text-xs font-semibold tnum text-[#D97706]">{money(r.endBalance, "USD", { accounting: true })} remaining</span>
                  </div>
                )}
                {isShortfall && (
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center rounded-full bg-[#D97706]/10 px-2.5 py-0.5 text-xs font-semibold text-[#D97706]" data-testid={`verdict-shortfall-${r.student.id}`}>Short by {money(shortBy, "USD", { accounting: true })}</span>
                    <span className="text-xs tnum text-[#6b7280]">covers ~{r.pctCoverage}%</span>
                  </div>
                )}
              </div>
              {/* Details */}
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#6b7280]">Current balance</span>
                  <span className="tnum font-semibold text-[#1f2937]">{money(r.balance, "USD", { accounting: true })}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#6b7280]">Remaining cost</span>
                  <span className="tnum font-semibold text-[#1f2937]">{money(r.totalCostRemaining, "USD", { accounting: true })}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#6b7280]">Time remaining</span>
                  <span className="tnum font-semibold text-[#1f2937]">{r.monthsRemaining != null ? `${(r.monthsRemaining / 12).toFixed(1)} years` : "\u2014"}</span>
                </div>
              </div>
              <div className="mt-3 border-t border-[#E9E9EB] pt-2">
                <button
                  onClick={() => { selectStudent(r.student.id); nav("/projections"); }}
                  className="flex items-center gap-1 text-xs font-semibold text-[#034AA2] hover:underline"
                  data-testid={`projections-view-${r.student.id}`}
                >
                  View projection <ArrowRight size={12} weight="bold" />
                </button>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Footnote */}
      <p className="mt-3 text-xs leading-relaxed text-[#6b7280]" data-testid="projections-footnote">
        Applies to higher education only. Each beneficiary&apos;s projection starts from three assumptions: the plan&apos;s expected annual growth (3%, kept conservative while the beneficiary is enrolled), an annual education-cost inflation rate (3%), and any planned break in that beneficiary&apos;s enrollment. Open a beneficiary&apos;s View projection to adjust these.
      </p>
    </div>
  );
}
